import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import api from '@/services/api';
import { UndoToastContainer, type PendingAction } from '@/components/UndoToast';

interface EmailAccount {
  googleId: string;
  email: string;
  name?: string;
  picture?: string;
}

type EmailCategory = 'marketing' | 'transactional' | 'notification' | 'social' | 'newsletter' | 'service' | 'unknown';
type Recommendation = 'delete' | 'unsubscribe' | 'keep' | 'review';
type Priority = 0 | 1 | 2 | 3 | 4 | 5;

interface PriorityInfo {
  level: Priority;
  label: string;
  description: string;
}

interface DeadlineInfo {
  hasDeadline: boolean;
  deadlineText?: string;
  urgency: 'immediate' | 'today' | 'this_week' | 'future' | 'none';
}

interface Subscription {
  sender: string;
  senderEmail: string;
  messageCount: number;
  oldestMessage: string;
  newestMessage: string;
  unsubscribeMethod: 'mailto' | 'url' | 'filter' | null;
  unsubscribeUrl?: string;
  sampleSubjects: string[];
  accountEmail: string;
  googleId: string;
  // Enhanced analysis
  category: EmailCategory;
  estimatedMinutesPerMonth: number;
  hasPushApp: boolean;
  recommendation: Recommendation;
  reasonCode: string;
  priority: PriorityInfo;
  // New fields
  hasUnsubscribeHeader: boolean;
  supportsOneClick: boolean;
  oneClickUrl?: string;
  isCriticalSender: boolean;
  criticalCategory?: 'healthcare' | 'government' | 'financial' | 'emergency';
  deadline: DeadlineInfo;
}

interface SubscriptionSummary {
  totalSenders: number;
  totalEmails: number;
  estimatedMinutesSaved: number;
  byRecommendation: Record<Recommendation, number>;
  byCategory: Record<EmailCategory, number>;
  duplicatePushCount: number;
}

interface SubscriptionData {
  accounts: EmailAccount[];
  subscriptions: Subscription[];
  summary?: SubscriptionSummary;
  message?: string;
  scannedAt?: string;
}

type UnsubscribeAction = 'filter' | 'filter_delete' | 'archive' | 'delete';
type SortOption = 'priority' | 'recommendation' | 'time' | 'count' | 'recent' | 'sender';

// Category display config
const CATEGORY_CONFIG: Record<EmailCategory, { label: string; emoji: string; color: string }> = {
  marketing: { label: 'Marketing', emoji: '📢', color: 'text-orange-400' },
  transactional: { label: 'Receipt', emoji: '🧾', color: 'text-green-400' },
  notification: { label: 'Notification', emoji: '🔔', color: 'text-blue-400' },
  social: { label: 'Social', emoji: '👥', color: 'text-purple-400' },
  newsletter: { label: 'Newsletter', emoji: '📰', color: 'text-cyan-400' },
  service: { label: 'Service', emoji: '⚙️', color: 'text-gray-400' },
  unknown: { label: 'Other', emoji: '📧', color: 'text-white/50' },
};

const RECOMMENDATION_CONFIG: Record<Recommendation, { label: string; color: string; bgColor: string }> = {
  delete: { label: 'Delete', color: 'text-red-400', bgColor: 'bg-red-500/20' },
  unsubscribe: { label: 'Unsubscribe', color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  review: { label: 'Review', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  keep: { label: 'Keep', color: 'text-green-400', bgColor: 'bg-green-500/20' },
};

const REASON_MESSAGES: Record<string, string> = {
  marketing_noise: 'Pure marketing noise',
  marketing_no_unsub: 'Marketing without unsubscribe',
  duplicate_push: 'Also sends push notifications',
  social_use_app: 'Use the app instead',
  high_volume: 'High volume - worth checking',
  transactional_records: 'Keep for records',
  service_important: 'Important service info',
  newsletter_value: 'May have value',
  notification_overload: 'Too many notifications',
  notification_moderate: 'Moderate notifications',
  unknown: 'Review manually',
};

// Priority display config (P0 = Critical, P5 = Noise)
const PRIORITY_CONFIG: Record<Priority, { color: string; bgColor: string; borderColor: string }> = {
  0: { color: 'text-red-500', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/50' },
  1: { color: 'text-orange-500', bgColor: 'bg-orange-500/20', borderColor: 'border-orange-500/50' },
  2: { color: 'text-yellow-500', bgColor: 'bg-yellow-500/20', borderColor: 'border-yellow-500/50' },
  3: { color: 'text-blue-400', bgColor: 'bg-blue-500/20', borderColor: 'border-blue-500/50' },
  4: { color: 'text-gray-400', bgColor: 'bg-gray-500/20', borderColor: 'border-gray-500/50' },
  5: { color: 'text-gray-500', bgColor: 'bg-gray-600/20', borderColor: 'border-gray-600/50' },
};

// Critical sender badges
const CRITICAL_CONFIG: Record<string, { emoji: string; label: string; color: string }> = {
  healthcare: { emoji: '🏥', label: 'Health', color: 'text-red-400' },
  government: { emoji: '🏛️', label: 'Gov', color: 'text-blue-400' },
  financial: { emoji: '🏦', label: 'Finance', color: 'text-green-400' },
  emergency: { emoji: '🚨', label: 'Emergency', color: 'text-red-500' },
};

// Deadline urgency badges
const URGENCY_CONFIG: Record<string, { color: string; bgColor: string }> = {
  immediate: { color: 'text-red-400', bgColor: 'bg-red-500/20' },
  today: { color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  this_week: { color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  future: { color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
};

// Generate consistent color for account
function getAccountColor(email: string): string {
  const colors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-green-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-cyan-500',
  ];
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export function Subscriptions() {
  const [data, setData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [connecting, setConnecting] = useState(false);

  // Undo cushion state
  const [undoDelaySeconds, setUndoDelaySeconds] = useState<number>(10);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const pendingActionsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Filters
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortOption>('priority');
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<EmailCategory | 'all'>('all');

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<SubscriptionData>('/email/subscriptions');
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreferences = useCallback(async () => {
    try {
      const response = await api.get<{ preferences: { undoDelaySeconds?: number } }>('/settings');
      if (response.data.preferences.undoDelaySeconds !== undefined) {
        setUndoDelaySeconds(response.data.preferences.undoDelaySeconds);
      }
    } catch (err) {
      // Default to 10 seconds if we can't fetch preferences
      console.error('Failed to fetch preferences:', err);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
    fetchPreferences();
  }, [fetchSubscriptions, fetchPreferences]);

  // Cleanup pending actions on unmount
  useEffect(() => {
    return () => {
      pendingActionsRef.current.forEach((timer) => clearTimeout(timer));
      pendingActionsRef.current.clear();
    };
  }, []);

  // Filtered and sorted subscriptions
  const filteredSubscriptions = useMemo(() => {
    if (!data) return [];

    let subs = data.subscriptions.filter(s => !completed.has(s.senderEmail));

    // Account filter
    if (accountFilter !== 'all') {
      subs = subs.filter(s => s.googleId === accountFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      subs = subs.filter(s => s.category === categoryFilter);
    }

    // Sort
    const recommendationOrder = { delete: 0, unsubscribe: 1, review: 2, keep: 3 };
    switch (sortBy) {
      case 'priority':
        // Sort by priority level (P0 first = important, P5 last = noise)
        // Lower priority level = more important, so show noise (P5) at top for cleanup
        subs.sort((a, b) => {
          const priorityDiff = (b.priority?.level ?? 3) - (a.priority?.level ?? 3);
          if (priorityDiff !== 0) return priorityDiff;
          return b.estimatedMinutesPerMonth - a.estimatedMinutesPerMonth;
        });
        break;
      case 'recommendation':
        subs.sort((a, b) => {
          const orderDiff = recommendationOrder[a.recommendation] - recommendationOrder[b.recommendation];
          if (orderDiff !== 0) return orderDiff;
          return b.estimatedMinutesPerMonth - a.estimatedMinutesPerMonth;
        });
        break;
      case 'time':
        subs.sort((a, b) => b.estimatedMinutesPerMonth - a.estimatedMinutesPerMonth);
        break;
      case 'count':
        subs.sort((a, b) => b.messageCount - a.messageCount);
        break;
      case 'recent':
        subs.sort((a, b) => new Date(b.newestMessage).getTime() - new Date(a.newestMessage).getTime());
        break;
      case 'sender':
        subs.sort((a, b) => a.sender.localeCompare(b.sender));
        break;
    }

    return subs;
  }, [data, accountFilter, categoryFilter, sortBy, completed]);

  // Stats per account
  const accountStats = useMemo(() => {
    if (!data) return new Map();
    const stats = new Map<string, { count: number; emails: number }>();
    for (const sub of data.subscriptions) {
      const existing = stats.get(sub.googleId) || { count: 0, emails: 0 };
      existing.count += 1;
      existing.emails += sub.messageCount;
      stats.set(sub.googleId, existing);
    }
    return stats;
  }, [data]);

  const handleConnectAccount = async () => {
    try {
      setConnecting(true);
      const response = await api.get<{ authUrl: string }>('/auth/google/connect');
      window.location.href = response.data.authUrl;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start connection');
      setConnecting(false);
    }
  };

  const toggleSelect = (email: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelected(newSelected);
  };

  const selectAll = () => {
    const allEmails = filteredSubscriptions.map(s => s.senderEmail);
    setSelected(new Set(allEmails));
  };

  const selectNone = () => {
    setSelected(new Set());
  };

  // Send feedback to API for AI learning
  const sendFeedback = useCallback(async (senderEmail: string, action: 'keep' | 'delete' | 'important') => {
    try {
      await api.post('/settings/feedback', { senderEmail, action });
    } catch (err) {
      console.error('Failed to record feedback:', err);
    }
  }, []);

  // Execute the actual unsubscribe API call
  const executeUnsubscribe = useCallback(async (sub: Subscription, action: UnsubscribeAction) => {
    const key = sub.senderEmail;
    try {
      await api.post('/email/unsubscribe', {
        googleId: sub.googleId,
        senderEmail: sub.senderEmail,
        method: action,
      });

      // Record feedback for AI learning
      await sendFeedback(sub.senderEmail, 'delete');

      setCompleted(prev => new Set(prev).add(key));
      setSelected(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    } catch (err: any) {
      console.error('Unsubscribe failed:', err);
      setError(`Failed to unsubscribe from ${sub.sender}`);
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  // Handle undo action completion (from UndoToast)
  const handleUndoComplete = useCallback((id: string, executed: boolean) => {
    setPendingActions(prev => prev.filter(a => a.id !== id));
    if (!executed) {
      // Action was undone - remove from processing
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  // Undo all pending actions
  const handleUndoAll = useCallback(() => {
    const ids = pendingActions.map(a => a.id);
    setPendingActions([]);
    setProcessing(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, [pendingActions]);

  const handleUnsubscribe = useCallback((sub: Subscription, action: UnsubscribeAction) => {
    const key = sub.senderEmail;
    setProcessing(prev => new Set(prev).add(key));

    // If undo delay is 0, execute immediately
    if (undoDelaySeconds === 0) {
      executeUnsubscribe(sub, action);
      return;
    }

    // Queue action with undo cushion
    const pendingAction: PendingAction = {
      id: key,
      label: `Unsubscribing from ${sub.sender}`,
      description: action === 'filter_delete' ? 'Delete & block' : action === 'filter' ? 'Auto-archive' : action,
      onExecute: async () => {
        await executeUnsubscribe(sub, action);
      },
      onUndo: () => {
        // Nothing to undo yet - just cancel the pending action
      },
    };

    setPendingActions(prev => [...prev, pendingAction]);
  }, [undoDelaySeconds, executeUnsubscribe]);

  // Execute the actual one-click unsubscribe API call
  const executeOneClickUnsubscribe = useCallback(async (sub: Subscription) => {
    if (!sub.oneClickUrl) return;

    const key = sub.senderEmail;
    try {
      const response = await api.post<{ success: boolean; message: string }>('/email/unsubscribe/oneclick', {
        oneClickUrl: sub.oneClickUrl,
        senderEmail: sub.senderEmail,
      });

      if (response.data.success) {
        // Record feedback for AI learning
        await sendFeedback(sub.senderEmail, 'delete');

        setCompleted(prev => new Set(prev).add(key));
        setSelected(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      } else {
        // Fall back to manual unsubscribe link
        if (sub.unsubscribeUrl) {
          window.open(sub.unsubscribeUrl, '_blank');
        }
      }
    } catch (err: any) {
      console.error('One-click unsubscribe failed:', err);
      // Fall back to opening URL
      if (sub.unsubscribeUrl) {
        window.open(sub.unsubscribeUrl, '_blank');
      }
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  // One-click unsubscribe (RFC 8058)
  const handleOneClickUnsubscribe = useCallback((sub: Subscription) => {
    if (!sub.oneClickUrl) return;

    const key = sub.senderEmail;
    setProcessing(prev => new Set(prev).add(key));

    // If undo delay is 0, execute immediately
    if (undoDelaySeconds === 0) {
      executeOneClickUnsubscribe(sub);
      return;
    }

    // Queue action with undo cushion
    const pendingAction: PendingAction = {
      id: key,
      label: `1-click unsubscribe from ${sub.sender}`,
      description: 'RFC 8058 one-click',
      onExecute: async () => {
        await executeOneClickUnsubscribe(sub);
      },
      onUndo: () => {
        // Nothing to undo yet - just cancel the pending action
      },
    };

    setPendingActions(prev => [...prev, pendingAction]);
  }, [undoDelaySeconds, executeOneClickUnsubscribe]);

  // Note: sendFeedback is defined earlier in the file

  // Mark sender as "keep" - important for AI learning
  const handleKeep = async (sub: Subscription) => {
    const key = sub.senderEmail;
    setProcessing(prev => new Set(prev).add(key));

    try {
      await sendFeedback(sub.senderEmail, 'keep');
      setCompleted(prev => new Set(prev).add(key));
      setSelected(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    } catch (err: any) {
      console.error('Keep feedback failed:', err);
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleBulkUnsubscribe = async (action: UnsubscribeAction) => {
    if (!data || selected.size === 0) return;

    // Group by googleId
    const byAccount = new Map<string, string[]>();
    for (const sub of data.subscriptions) {
      if (selected.has(sub.senderEmail)) {
        const list = byAccount.get(sub.googleId) || [];
        list.push(sub.senderEmail);
        byAccount.set(sub.googleId, list);
      }
    }

    setProcessing(new Set(selected));

    try {
      for (const [googleId, emails] of byAccount) {
        await api.post('/email/unsubscribe/batch', {
          googleId,
          senders: emails.map(email => ({ email, method: action })),
        });
      }

      setCompleted(prev => new Set([...prev, ...selected]));
      setSelected(new Set());
    } catch (err: any) {
      console.error('Bulk unsubscribe failed:', err);
      setError('Bulk unsubscribe failed. Some actions may have completed.');
    } finally {
      setProcessing(new Set());
    }
  };

  // No accounts connected
  if (!loading && data && data.accounts.length === 0) {
    return (
      <div className="p-4 space-y-6">
        <header className="pt-2">
          <h1 className="text-2xl font-bold">Email Subscriptions</h1>
          <p className="text-white/50 text-sm mt-1">
            Connect your email accounts to find and unsubscribe from noisy senders
          </p>
        </header>

        <div className="card-raised text-center py-12">
          <span className="text-5xl mb-4 block">📬</span>
          <h3 className="text-xl font-bold mb-2">Connect Your Email</h3>
          <p className="text-white/50 mb-6 max-w-sm mx-auto">
            Link your Google accounts to scan for newsletters and subscriptions you might want to clean up.
          </p>
          <button
            onClick={handleConnectAccount}
            disabled={connecting}
            className="btn-primary px-6"
          >
            {connecting ? 'Connecting...' : 'Connect Google Account'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <header className="pt-2">
        <h1 className="text-2xl font-bold">Email Subscriptions</h1>
        <p className="text-white/50 text-sm mt-1">
          {data?.subscriptions.length || 0} noisy senders found
        </p>
      </header>

      {/* Summary stats */}
      {data?.summary && (
        <div className="grid grid-cols-2 gap-3">
          <div className="card bg-red-500/10 border border-red-500/20">
            <div className="text-2xl font-bold text-red-400">
              {Math.round(data.summary.estimatedMinutesSaved / 60)}h
            </div>
            <div className="text-xs text-white/50">time/month on noise</div>
          </div>
          <div className="card bg-orange-500/10 border border-orange-500/20">
            <div className="text-2xl font-bold text-orange-400">
              {data.summary.duplicatePushCount}
            </div>
            <div className="text-xs text-white/50">duplicate of push</div>
          </div>
          <div className="card bg-accent-primary/10 border border-accent-primary/20">
            <div className="text-2xl font-bold text-accent-primary">
              {data.summary.byRecommendation.delete + data.summary.byRecommendation.unsubscribe}
            </div>
            <div className="text-xs text-white/50">safe to remove</div>
          </div>
          <div className="card bg-green-500/10 border border-green-500/20">
            <div className="text-2xl font-bold text-green-400">
              {data.summary.totalEmails}
            </div>
            <div className="text-xs text-white/50">emails scanned</div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-accent-danger/20 text-accent-danger p-3 rounded-lg text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Connected accounts summary */}
      {data && data.accounts.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          <button
            onClick={() => setAccountFilter('all')}
            className={clsx(
              'flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-bold transition-all shadow-sm',
              accountFilter === 'all'
                ? 'bg-accent-primary text-white shadow-accent-primary/30'
                : 'bg-white/15 text-white hover:bg-white/25 border border-white/20'
            )}
          >
            All accounts ({data.subscriptions.length})
          </button>
          {data.accounts.map((account) => {
            const stats = accountStats.get(account.googleId);
            return (
              <button
                key={account.googleId}
                onClick={() => setAccountFilter(account.googleId)}
                className={clsx(
                  'flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-all shadow-sm',
                  accountFilter === account.googleId
                    ? 'bg-accent-primary text-white shadow-accent-primary/30'
                    : 'bg-white/15 text-white hover:bg-white/25 border border-white/20'
                )}
              >
                <span className={clsx('w-2.5 h-2.5 rounded-full', getAccountColor(account.email))} />
                <span className="truncate max-w-[120px]">{account.email.split('@')[0]}</span>
                <span className="text-white/70 font-semibold">({stats?.count || 0})</span>
              </button>
            );
          })}
          <button
            onClick={handleConnectAccount}
            disabled={connecting}
            className="flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-bold bg-white/10 text-accent-primary hover:bg-white/20 transition-all border border-accent-primary/30"
          >
            + Add
          </button>
        </div>
      )}

      {loading ? (
        <div className="card py-12 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-white/50">Scanning your emails...</p>
          <p className="text-white/30 text-sm mt-1">This may take a minute</p>
        </div>
      ) : (
        <>
          {/* Controls bar */}
          {filteredSubscriptions.length > 0 && (
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <button onClick={selectAll} className="text-sm text-accent-primary">
                  Select all
                </button>
                <span className="text-white/20">|</span>
                <button onClick={selectNone} className="text-sm text-white/50">
                  Clear
                </button>
                {selected.size > 0 && (
                  <span className="text-sm text-white/50">
                    {selected.size} selected
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(
                  'text-sm px-4 py-2 rounded-full font-bold transition-all',
                  showFilters
                    ? 'bg-accent-primary text-white shadow-sm'
                    : 'bg-white/15 text-white border border-white/20 hover:bg-white/25'
                )}
              >
                Sort
              </button>
            </div>
          )}

          {/* Sort options */}
          {showFilters && (
            <div className="space-y-3 py-2">
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'priority', label: 'Priority (P0-P5)' },
                  { value: 'recommendation', label: 'Action' },
                  { value: 'time', label: 'Time impact' },
                  { value: 'count', label: 'Most emails' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value as SortOption)}
                    className={clsx(
                      'px-4 py-2 rounded-full text-sm font-bold transition-all',
                      sortBy === option.value
                        ? 'bg-white/25 text-white border border-white/30'
                        : 'bg-white/10 text-white/80 hover:bg-white/15 border border-white/10'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {/* Category filter */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCategoryFilter('all')}
                  className={clsx(
                    'px-3 py-1.5 rounded-full text-xs font-semibold transition-all',
                    categoryFilter === 'all'
                      ? 'bg-accent-primary text-white'
                      : 'bg-white/10 text-white/70 hover:bg-white/15'
                  )}
                >
                  All types
                </button>
                {(['marketing', 'notification', 'social', 'newsletter', 'transactional'] as EmailCategory[]).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={clsx(
                      'px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1',
                      categoryFilter === cat
                        ? 'bg-accent-primary text-white'
                        : 'bg-white/10 text-white/70 hover:bg-white/15'
                    )}
                  >
                    <span>{CATEGORY_CONFIG[cat].emoji}</span>
                    <span>{CATEGORY_CONFIG[cat].label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Subscription list */}
          <div className="space-y-3">
            {filteredSubscriptions.map((sub) => {
              const key = sub.senderEmail;
              const isSelected = selected.has(key);
              const isProcessing = processing.has(key);
              const isCompleted = completed.has(key);
              const account = data?.accounts.find(a => a.googleId === sub.googleId);

              return (
                <div
                  key={key}
                  className={clsx(
                    'card transition-all duration-200',
                    isCompleted && 'opacity-50',
                    isSelected && !isCompleted && 'ring-2 ring-accent-primary'
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => !isCompleted && toggleSelect(key)}
                      disabled={isCompleted || isProcessing}
                      className={clsx(
                        'w-6 h-6 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors',
                        isCompleted
                          ? 'bg-accent-success border-accent-success text-white'
                          : isSelected
                          ? 'bg-accent-primary border-accent-primary text-white'
                          : 'border-white/30 hover:border-white/50'
                      )}
                    >
                      {(isSelected || isCompleted) && (
                        <span className="text-sm">✓</span>
                      )}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header row with sender and recommendation */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {data && data.accounts.length > 1 && (
                            <span
                              className={clsx('w-2 h-2 rounded-full flex-shrink-0', getAccountColor(sub.accountEmail))}
                              title={sub.accountEmail}
                            />
                          )}
                          <h3 className="font-medium truncate">{sub.sender}</h3>
                        </div>
                        {/* Priority + Recommendation badges */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {sub.priority && (
                            <span
                              className={clsx(
                                'px-1.5 py-0.5 rounded text-xs font-bold border',
                                PRIORITY_CONFIG[sub.priority.level].bgColor,
                                PRIORITY_CONFIG[sub.priority.level].color,
                                PRIORITY_CONFIG[sub.priority.level].borderColor
                              )}
                              title={sub.priority.description}
                            >
                              {sub.priority.label}
                            </span>
                          )}
                          <span
                            className={clsx(
                              'px-2 py-0.5 rounded-full text-xs font-bold',
                              RECOMMENDATION_CONFIG[sub.recommendation].bgColor,
                              RECOMMENDATION_CONFIG[sub.recommendation].color
                            )}
                          >
                            {RECOMMENDATION_CONFIG[sub.recommendation].label}
                          </span>
                        </div>
                      </div>

                      {/* Email and category row */}
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-white/50 truncate">{sub.senderEmail}</p>
                        <span className={clsx('text-xs flex-shrink-0', CATEGORY_CONFIG[sub.category].color)}>
                          {CATEGORY_CONFIG[sub.category].emoji} {CATEGORY_CONFIG[sub.category].label}
                        </span>
                      </div>

                      {/* Stats row */}
                      <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                        <span className="text-white/70">
                          <span className="text-accent-warning font-semibold">{sub.messageCount}</span> emails
                        </span>
                        <span className="text-white/30">•</span>
                        <span className="text-white/70">
                          <span className="text-red-400 font-semibold">~{sub.estimatedMinutesPerMonth}</span> min/mo
                        </span>
                        {sub.hasPushApp && (
                          <>
                            <span className="text-white/30">•</span>
                            <span className="text-orange-400 font-medium">📱 Also sends push</span>
                          </>
                        )}
                        {/* Critical sender badge */}
                        {sub.isCriticalSender && sub.criticalCategory && (
                          <>
                            <span className="text-white/30">•</span>
                            <span className={clsx('font-medium', CRITICAL_CONFIG[sub.criticalCategory].color)}>
                              {CRITICAL_CONFIG[sub.criticalCategory].emoji} {CRITICAL_CONFIG[sub.criticalCategory].label}
                            </span>
                          </>
                        )}
                        {/* Deadline badge */}
                        {sub.deadline?.hasDeadline && sub.deadline.urgency !== 'none' && (
                          <>
                            <span className="text-white/30">•</span>
                            <span className={clsx(
                              'px-1.5 py-0.5 rounded text-xs font-medium',
                              URGENCY_CONFIG[sub.deadline.urgency]?.bgColor,
                              URGENCY_CONFIG[sub.deadline.urgency]?.color
                            )}>
                              ⏰ {sub.deadline.deadlineText || sub.deadline.urgency}
                            </span>
                          </>
                        )}
                        {/* No unsubscribe warning */}
                        {!sub.hasUnsubscribeHeader && sub.recommendation !== 'keep' && (
                          <>
                            <span className="text-white/30">•</span>
                            <span className="text-red-400/80 font-medium" title="This sender doesn't provide an unsubscribe link - may be shady">
                              ⚠️ No unsubscribe
                            </span>
                          </>
                        )}
                      </div>

                      {/* Reason + Priority description */}
                      <p className="text-xs text-white/40 mt-1">
                        {sub.priority?.description || REASON_MESSAGES[sub.reasonCode] || sub.reasonCode}
                      </p>

                      {/* Sample subject */}
                      <p className="text-xs text-white/25 mt-1 line-clamp-1 italic">
                        "{sub.sampleSubjects[0]}"
                      </p>

                      {data && data.accounts.length > 1 && (
                        <p className="text-xs text-white/20 mt-1 flex items-center gap-1">
                          <span className={clsx('w-1.5 h-1.5 rounded-full', getAccountColor(sub.accountEmail))} />
                          {sub.accountEmail}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {!isCompleted && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-white/10">
                      {/* Primary action based on recommendation */}
                      {sub.recommendation === 'delete' ? (
                        <button
                          onClick={() => handleUnsubscribe(sub, 'filter_delete')}
                          disabled={isProcessing}
                          className="flex-1 text-sm py-2 px-3 rounded-lg font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                        >
                          {isProcessing ? 'Deleting...' : '🗑️ Delete & block'}
                        </button>
                      ) : sub.recommendation === 'unsubscribe' ? (
                        <button
                          onClick={() => handleUnsubscribe(sub, 'filter_delete')}
                          disabled={isProcessing}
                          className="flex-1 text-sm py-2 px-3 rounded-lg font-medium bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors"
                        >
                          {isProcessing ? 'Working...' : '🚫 Stop & delete'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnsubscribe(sub, 'filter')}
                          disabled={isProcessing}
                          className="btn-secondary flex-1 text-sm py-2"
                        >
                          {isProcessing ? 'Working...' : '📁 Auto-archive'}
                        </button>
                      )}

                      {/* Secondary action - archive if primary is delete */}
                      {sub.recommendation === 'delete' && (
                        <button
                          onClick={() => handleUnsubscribe(sub, 'filter')}
                          disabled={isProcessing}
                          className="btn-ghost text-sm py-2 px-3"
                        >
                          Archive instead
                        </button>
                      )}

                      {/* One-click unsubscribe if supported */}
                      {sub.supportsOneClick && sub.oneClickUrl && (
                        <button
                          onClick={() => handleOneClickUnsubscribe(sub)}
                          disabled={isProcessing}
                          className="text-sm py-2 px-3 rounded-lg font-medium bg-accent-primary/20 text-accent-primary hover:bg-accent-primary/30 transition-colors"
                        >
                          {isProcessing ? 'Unsubscribing...' : '✨ 1-click unsub'}
                        </button>
                      )}
                      {/* Manual unsubscribe link if available (more prominent) */}
                      {sub.unsubscribeUrl && !sub.supportsOneClick && sub.recommendation !== 'delete' && (
                        <a
                          href={sub.unsubscribeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm py-2 px-3 rounded-lg font-medium bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/20 transition-colors text-center"
                        >
                          Unsubscribe ↗
                        </a>
                      )}
                      {/* Keep button - mark as important for AI learning */}
                      <button
                        onClick={() => handleKeep(sub)}
                        disabled={isProcessing}
                        className="text-sm py-2 px-3 rounded-lg font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                        title="Mark as important - I want to keep receiving these"
                      >
                        {isProcessing ? '...' : '✓ Keep'}
                      </button>
                    </div>
                  )}
                  {isCompleted && (
                    <div className="mt-3 pt-3 border-t border-white/10 text-center text-accent-success text-sm">
                      ✅ Done! Emails will be handled automatically.
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Empty states */}
          {filteredSubscriptions.length === 0 && data && data.subscriptions.length > 0 && (
            <div className="card text-center py-8">
              <p className="text-white/50">No subscriptions match this filter</p>
              <button
                onClick={() => setAccountFilter('all')}
                className="text-accent-primary text-sm mt-2"
              >
                Show all accounts
              </button>
            </div>
          )}

          {data?.subscriptions.length === 0 && (
            <div className="card-raised text-center py-12">
              <span className="text-5xl mb-4 block">✨</span>
              <h3 className="text-xl font-bold mb-2">Inbox Zero Hero!</h3>
              <p className="text-white/50">
                No noisy senders found. Your inbox is already clean.
              </p>
            </div>
          )}
        </>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-20 left-0 right-0 p-4 bg-surface border-t border-white/10 safe-area-inset-bottom">
          <div className="flex gap-3 max-w-lg mx-auto">
            <button
              onClick={() => handleBulkUnsubscribe('filter_delete')}
              disabled={processing.size > 0}
              className="flex-1 py-3 px-4 rounded-lg font-semibold bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
            >
              {processing.size > 0
                ? `Processing ${processing.size}...`
                : `🗑️ Delete ${selected.size} senders`}
            </button>
            <button
              onClick={() => handleBulkUnsubscribe('filter')}
              disabled={processing.size > 0}
              className="btn-secondary py-3 px-4"
            >
              📁 Archive
            </button>
          </div>
        </div>
      )}

      {/* Undo toast container */}
      <UndoToastContainer
        pendingActions={pendingActions}
        delaySeconds={undoDelaySeconds}
        onComplete={handleUndoComplete}
        onUndoAll={handleUndoAll}
      />
    </div>
  );
}
